import { useMutation } from '@apollo/client';
import { Grid } from '@mui/material';

import { Formik } from 'formik';
import { omit, omitBy, pick } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import CREATE_MUTATION from 'gql/mutations/MessagingService/createSystemNotification.gql';
import UPDATE_MUTATION from 'gql/mutations/MessagingService/updateSystemNotification.gql';

import MultistepForm from 'app/components/MultistepForm';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { SytemNotification } from 'types/AppTypes';
import { validateForm } from 'utils/FormErrorHelper';

import { SystemNotificationInformation } from './components/SystemNotificationInformation';
import { systemNotificationValidationSchema } from './validation';

interface Props {
  closeHandler: Function;
  setDisabledCancel?: Function;
  systemNotification?: SytemNotification;
}

export function SystemNotificationModal(props: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [submitting, setSubmitting] = useState(false);

  const [createSystemNotification] = useMutation(CREATE_MUTATION);
  const [updateSystemNotification] = useMutation(UPDATE_MUTATION);

  const initialValues = () => {
    return props.systemNotification
      ? {
          ...pick(props.systemNotification, [
            'id',
            'headline',
            'content',
            'startDate',
            'endDate',
            'type',
            'isActive'
          ])
        }
      : { isActive: false };
  };

  const steps = ['System Notification Creation'];

  const handleSaveClick = async values => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }

    const omitFields = ['id'];
    let saveBody = {
      ...omit(values, omitFields)
    };

    if (values.id) {
      // * UPDATE System-Notification
      try {
        await updateSystemNotification({
          variables: { id: values.id, data: omitBy(saveBody, v => v === '') },
          refetchQueries: ['GetSystemNotifications']
        });
        dispatch(
          AlertActions.success(
            t(
              translations.pages.systemNotificationsSettings.alerts.success.updateSystemNotification()
            )
          )
        );
      } catch (err) {
        dispatch(
          AlertActions.error(
            t(
              translations.pages.systemNotificationsSettings.alerts.error.updateSystemNotification()
            )
          )
        );
      }
    } else {
      // * CREATE System-Notification
      try {
        await createSystemNotification({
          variables: { data: omitBy(saveBody, v => v === null || v === '') },
          refetchQueries: ['GetSystemNotifications']
        });
        dispatch(
          AlertActions.success(
            t(
              translations.pages.systemNotificationsSettings.alerts.success.createSystemNotification()
            )
          )
        );
      } catch (err) {
        dispatch(
          AlertActions.error(
            t(
              translations.pages.systemNotificationsSettings.alerts.error.createSystemNotification()
            )
          )
        );
      }
    }

    props.closeHandler();
    setSubmitting(false);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(false);
    }
  };

  const SystemNotificationModal = ({ step }) => {
    switch (step) {
      case 0:
        return <SystemNotificationInformation />;
      default:
        return null;
    }
  };

  return (
    <Grid item className="systemNotificationModal" paddingX={2}>
      <Formik
        isInitialValid={!!props.systemNotification}
        initialValues={initialValues()}
        onSubmit={values => handleSaveClick(values)}
        validationSchema={systemNotificationValidationSchema(t)}
        validate={values => {
          return validateForm(values, systemNotificationValidationSchema(t), dispatch);
        }}
      >
        <MultistepForm
          submitting={submitting}
          steps={steps}
          onCancel={props.closeHandler}
          hideStepper
        >
          {activeStep => (
            <Grid container>
              <SystemNotificationModal step={activeStep} />
            </Grid>
          )}
        </MultistepForm>
      </Formik>
    </Grid>
  );
}
